import {
  Budget,
  BudgetCategory,
  BudgetItem,
  EmailOption,
  Vendor,
  PremisesData,
} from "../types/budgeting";
import { v4 as uuidv4 } from "uuid";

export const calculateTotalCost = (
  quantity: string,
  unitCost: string
): string => {
  const parsedQuantity = parseFloat(quantity) || 0;
  const parsedUnitCost = parseFloat(unitCost) || 0;
  return (parsedQuantity * parsedUnitCost).toFixed(2);
};

export const getStatusColor = (status: string | undefined): string => {
  if (!status) return "transparent";

  const statusColor = {
    Approved: "#4CAF50",
    Pending: "#FFC107",
    Rejected: "#F44336",
    "In Review": "#FF9800",
    "Waiting For Approvals": "#2196F3",
    Expired: "#9E9E9E",
    "Partially Approved": "#8BC34A",
  };

  return statusColor[status as keyof typeof statusColor] || "black";
};

export const createFullAddress = (premises: PremisesData): string => {
  return [premises.addressLine1, premises.townCity, premises.postcode]
    .filter(Boolean)
    .join(", ");
};

export const validateBudget = (
  budget: Budget
): { message: string; fieldId?: string } | null => {
  if (!budget.premises) {
    return { message: "Please select a valid premises", fieldId: "premises" };
  }

  if (!budget.premisesAddress) {
    return {
      message: "Please provide premises address",
      fieldId: "premisesAddress",
    };
  }

  if (budget.categories.length === 0) {
    return {
      message: "Please add at least one budget category",
      fieldId: "categories",
    };
  }

  for (
    let categoryIndex = 0;
    categoryIndex < budget.categories.length;
    categoryIndex++
  ) {
    const category = budget.categories[categoryIndex];

    if (category.category === "Select Category") {
      return {
        message: "Please select a category for all budget categories",
        fieldId: `category-${categoryIndex}`,
      };
    }

    if (category.items.length === 0) {
      return {
        message: "Please add at least one item to each category",
        fieldId: `category-${categoryIndex}-items`,
      };
    }

    for (let itemIndex = 0; itemIndex < category.items.length; itemIndex++) {
      const item = category.items[itemIndex];

      if (!item.name) {
        return {
          message: "Please provide a name for all items",
          fieldId: `item-${categoryIndex}-${itemIndex}-name`,
        };
      }

      if (!item.numbersNeeded) {
        return {
          message: "Please provide numbers needed for all items",
          fieldId: `item-${categoryIndex}-${itemIndex}-numbersNeeded`,
        };
      }

      if (!item.expectedCostPerUnit) {
        return {
          message: "Please provide expected cost for all items",
          fieldId: `item-${categoryIndex}-${itemIndex}-expectedCostPerUnit`,
        };
      }

      // New: Description required
      if (!item.description) {
        return {
          message: "Please provide a description for all items",
          fieldId: `item-${categoryIndex}-${itemIndex}-description`,
        };
      }

      // New: Justification required
      if (!item.justification) {
        return {
          message: "Please provide a justification for all items",
          fieldId: `item-${categoryIndex}-${itemIndex}-justification`,
        };
      }

      if (item.approvalStatus === "Rejected" && !item.riskMitigationPlan) {
        return {
          message:
            "Please provide a risk mitigation plan for all rejected items",
          fieldId: `item-${categoryIndex}-${itemIndex}-riskMitigationPlan`,
        };
      }
    }
  }

  return null;
};

export const getInitialBudget = (): Budget => {
  return {
    premises: "",
    premisesAddress: "",
    status: "Pending",
    categories: [],
    emailOptions: [{ email: "" }],
  };
};

export const getInitialBudgetCategory = (): BudgetCategory => ({
  id: uuidv4(),
  category: "Select Category",
  items: [],
  files: [],
  vendor: {
    name: "",
    phoneNumber: "",
    email: "",
    websiteAddress: "",
  },
});

export const getInitialBudgetItem = (): BudgetItem => {
  return {
    name: "",
    numbersNeeded: "",
    expectedCostPerUnit: "",
    totalCost: "0.00",
    description: "",
    justification: "",
    approvalStatus: "",
    riskMitigationPlan: "",
  };
};

export const mapBudgetDataFromResponse = (budgetData: any): Budget => {
  const data = budgetData.data || budgetData;

  return {
    id: data._id?.toString() || "",
    requestId: data.requestId || "",
    premises: data.premises || "",
    premisesAddress: data.premisesAddress || "",
    requestedBy: data.requestedBy || "",
    approvalManager: data.approvalManager || "",
    status: data.status || "Pending",
    totalCost: data.totalCost || 0,
    categories:
      data.categories?.map((category: any) => ({
        id: category._id?.toString(),
        category: category.category || "Select Category",
        vendor: {
          id: category.vendor?._id?.toString(),
          name: category.vendor?.name || "",
          phoneNumber: category.vendor?.phoneNumber || "",
          email: category.vendor?.email || "",
          websiteAddress: category.vendor?.websiteAddress || "",
        },
        items:
          category.items?.map((item: any) => ({
            id: item._id?.toString(),
            name: item.name || "",
            numbersNeeded: item.numbersNeeded?.toString() || "0",
            expectedCostPerUnit: item.expectedCostPerUnit?.toString() || "0",
            totalCost: item.totalCost?.toString() || "0",
            description: item.description || "",
            justification: item.justification || "",
            approvalStatus: item.approvalStatus || "Pending",
            riskMitigationPlan: item.riskMitigationPlan || "",
          })) || [],

        files: category.files || [],
      })) || [],
    emailOptions: data.emailOptions?.length
      ? data.emailOptions.map((option: any) => ({
          id: option._id?.toString(),
          email: option.email || "",
        }))
      : [{ email: "" }],
  };
};
